import type { Repository } from './repository.ts';
import type { Scope } from './query.ts';
import type { BatchWrite } from './batch.ts';

/**
 * Collections are reached through named accessors rather than raw paths, so
 * the storage layout is an adapter detail and every query is tenant-scoped by
 * construction. There is no method here that can read across companies.
 */
export interface DataPort {
  collection<T extends { id: string }>(scope: Scope, name: CollectionName): Repository<T>;

  /**
   * Apply every write, or none of them.
   *
   * A schedule cascade moves several items at once; a half-applied cascade
   * leaves dates that contradict their own dependencies, which is worse than
   * not cascading at all. The same holds for any multi-record state change,
   * so this is on the port rather than on one use case.
   *
   * Refuses more than `MAX_BATCH_WRITES`. Writing to the same document twice
   * in one batch is also refused: the outcome would depend on an ordering the
   * caller cannot see.
   */
  commit(writes: readonly BatchWrite[]): Promise<void>;
}

/** Collection names, kept central so adapters and rules cannot drift apart. */
export const COLLECTIONS = [
  'companies',
  'members',
  'roles',
  'invites',
  'contacts',
  'jobs',
  'workdayExceptions',
  'scheduleItems',
  'schedulePhases',
  'scheduleTemplates',
  'notifications',
  'mailQueue',
  'files',
  'folders',
  'dailyLogs',
  'todos',
  'comments',
  'tags',
  'changeOrders',
  'ownerChangeOrders',
  'selections',
  'allowances',
  // Phase 5. Company-level, not per job: the point of a chart of accounts
  // is to cost every job against the same one, and a per-job chart would
  // make the cross-job report it exists for impossible.
  'costCodeCategories',
  'costCodes',
  'catalogItems',
  'catalogAssemblies',
  /**
   * Consumable stock, company-level like the catalog it is stock *of*
   * (INV-01). "What do we have" is a company-wide question — a per-job
   * collection would make it a crawl, which is the same reasoning that
   * put `equipment` and `shifts` at the company level.
   *
   * The ledger is append-only and the level is derived from it by a
   * trigger. Neither is job-scoped: a movement names a job when it went
   * to one, in a field rather than in its path.
   */
  'stockMovements',
  'inventoryLevels',
  /**
   * The places a company keeps stock (INV2-06). Company-level for the
   * same reason the stock itself is: "where is this part" is a
   * company-wide question, and a job site is one of the answers rather
   * than a scope.
   */
  'stockLocations',
  'estimates',
  'budgetLines',
  'ownerBudgetLines',
  'purchaseOrders',
  'bills',
  'receipts',
  'changeLog',
  'internalNotes',
  'taxRates',
  'invoices',
  'ownerInvoices',
  'creditMemos',
  'deposits',
  // Company-level, like the chart of accounts: a safety notice is a
  // statement the company makes to its own people, not a fact about one
  // job. Acknowledgements hang beneath the notice they are of.
  'safetyNotices',
  'safetyAcknowledgements',
  // Company-level, like the chart of accounts and for the same reason: a
  // machine is owned by the company and worked across every job, so a
  // per-job register would make "where is EX-04 next week" impossible to
  // ask — which is the whole feature (SCRUM-209).
  'equipment',
  'equipmentTypes',
  'checklistTemplates',
  'warrantyClaims',
  /**
   * One document per section of a job's specification sheet, items inside
   * it — see `domain/entities/spec-section.ts` for why the sheet is not one
   * document and an item is not one either.
   */
  'specSections',
  /**
   * Requests for information (SCRUM-144). Job-scoped, like the records
   * they point at: a question belongs to the building it is about, and
   * the path is what lets a `list` rule enforce job assignment with one
   * read rather than one per returned row.
   */
  'rfis',
  /**
   * Bid packages (SCRUM-143). Job-scoped; the bids against one live in a
   * subcollection beneath it — see `RECORD_SCOPED_COLLECTIONS`.
   */
  'bidPackages',
  /**
   * What a trade reports after going out to a warranty claim (P8-03b).
   * Record-scoped beneath the claim: the trade writes this and the
   * office owns the claim, and a rule cannot blank a field.
   */
  'visitReports',
  /**
   * The bids on one package.
   *
   * **Record-scoped, and it is the confidentiality that decides it.**
   * Firestore rules are all-or-nothing per document, so prices kept on
   * the package would be in the bytes every invited trade received,
   * whatever the screen drew. A sibling document carries its own rule —
   * the same reason `internalNotes` is not a field on a purchase order.
   */
  'bids',
  /**
   * The time clock (SCRUM-142). **Company-level, not under the job**,
   * though every shift names one: the two questions the module exists to
   * answer — who is clocked in, and what is waiting to be approved — both
   * span every job, and a per-job collection would make each of them a
   * crawl. Same reasoning as `equipment`.
   */
  'shifts',
  /**
   * Derived, written only by a trigger. One row per machine per schedule
   * item, company-scoped so "where is EX-04 next Monday" is one query
   * rather than a crawl across every job's schedule — which the rules
   * would refuse anyway for anybody not assigned to all of them.
   */
  'equipmentBookings',
  /**
   * The assistant's meter (SCRUM-223). Written only by `askAssistant`;
   * a client that could write here could erase its own usage before an
   * invoice was computed from it.
   */
  'assistantUsage',
  /**
   * Company-level settings documents that are **Buildmate's** to set, not
   * the customer's — the assistant allowance is the first. An
   * administrator who could raise their own cap would be setting their
   * own bill.
   */
  'settings',
] as const;

/** Collections that live under a job rather than directly under a company. */
export const JOB_SCOPED_COLLECTIONS: readonly CollectionName[] = [
  'workdayExceptions',
  'scheduleItems',
  'schedulePhases',
  // Folders are per job; files are not. A company template and a job
  // document are the same kind of thing and want one collection to query by
  // tag, which is why `files` carries an optional `jobId` instead.
  'folders',
  // Job-scoped on purpose, unlike files: a log belongs to exactly one job,
  // and the path is the only thing a `list` rule can gate assignment on
  // without a document read per returned row.
  'dailyLogs',
  'todos',
  'comments',
  'changeOrders',
  'ownerChangeOrders',
  'selections',
  'allowances',
  // Per job, like change orders: an estimate prices one job and a budget
  // row belongs to one job's chart of accounts.
  'estimates',
  'budgetLines',
  'ownerBudgetLines',
  'purchaseOrders',
  'bills',
  'receipts',
  'invoices',
  'ownerInvoices',
  'creditMemos',
  'deposits',
  /**
   * **Missed once, and it reached dev** (2026-10-21). `warrantyClaims` was
   * written into the rules nested under `/jobs/{jobId}` and left out of
   * this list, so `collectionPath` addressed
   * `companies/{c}/warrantyClaims` — a path no rule matches — and every
   * read and write of a claim from a browser was denied while three test
   * suites stayed green. `scripts/collection-scopes.test.ts` now compares
   * this list against the rules file on every run.
   */
  'warrantyClaims',
  'specSections',
  'rfis',
  'bidPackages',
];

/**
 * Collections that live under one user rather than under the company.
 *
 * The identity has to be in the path, not in a field: Firestore rules cannot
 * restrict a `list` by field value, so a flat `notifications` collection
 * with a `recipientUid` would let any member list everybody else's.
 */
export const USER_SCOPED_COLLECTIONS: readonly CollectionName[] = ['notifications'];

/**
 * Collections that hang beneath one record rather than beneath a job.
 *
 * The change log (P5-14) and internal notes (P5-09e). `Scope.record`
 * carries the parent, and the reason each is a subcollection rather than a
 * job-level collection with a `recordKey` field is the same: it is then an
 * unfiltered query needing no composite index, and the child goes with the
 * parent when the parent is deleted.
 *
 * For `internalNotes` there is a second reason, and it is the whole point
 * of the collection. A purchase order is read by the **sub** it was given
 * to, and Firestore rules are all-or-nothing per document — a note stored
 * on the purchase order would be in the bytes their browser received,
 * whatever the component drew. A sibling document can carry its own rule.
 */
export const RECORD_SCOPED_COLLECTIONS: readonly CollectionName[] = [
  'changeLog',
  'internalNotes',
  // Beneath their notice, and for the first of the two reasons above:
  // reading one notice's acknowledgements is then an unfiltered query
  // needing no index, and a member's own acknowledgement sits at a path a
  // rule can match on without reading the document.
  'safetyAcknowledgements',
  // Beneath their package, for **both** reasons: one package's bids are
  // then an unfiltered query, and a price can carry a rule of its own so
  // one trade never reads another's.
  'bids',
  // Beneath the claim, because a trade writes it and the office owns the
  // claim. A field would have meant a rule freezing every other field on
  // the claim one by one — a list that stops being complete the next time
  // somebody adds one.
  'visitReports',
];

export type CollectionName = (typeof COLLECTIONS)[number];
